import { useState } from "react";

const FORM_URL = "https://readdy.ai/api/form/daftjq1tto0ceqqnhh90";

type Status = "idle" | "loading" | "success" | "error";

export default function Contact() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    // honeypot guard
    const honeypot = (data.get("company_alt") as string) || "";
    if (honeypot.trim()) {
      setStatus("success");
      form.reset();
      return;
    }

    // build payload without honeypot
    const payload = new URLSearchParams();
    data.forEach((value, key) => {
      if (key !== "company_alt") payload.append(key, value.toString());
    });

    setStatus("loading");
    setFormError("");
    try {
      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }
      const meta = (parsed?.meta ?? parsed) as Record<string, unknown> | undefined;
      const serverMsg =
        (meta?.message as string) ||
        (parsed?.message as string) ||
        (meta?.detail as string) ||
        responseText;
      const code = parsed?.code as string | undefined;

      if (response.ok && code === "OK") {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          typeof serverMsg === "string" && serverMsg
            ? serverMsg
            : "提交未成功，请稍后再试。"
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，请稍后重试。");
    }
  };

  return (
    <section id="contact" className="px-4 md:px-8 py-12 md:py-16 bg-background-100/70">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-5">
            <span className="inline-flex items-center gap-1.5 text-primary-600 font-medium text-sm">
              <i className="ri-chat-smile-fill"></i> 投稿 / 互动
            </span>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mt-2 leading-tight">
              你有一手猛料？
              <br />
              来和编辑部唠一唠
            </h2>
            <p className="text-foreground-500 text-sm md:text-[15px] leading-relaxed mt-4">
              提供线索、投稿选题、反馈更正，或只是想和我们聊聊最近吃的瓜，都可以通过下方表单联系我们。
              采纳的独家线索会在栏目中标注来源，如需匿名请在备注中说明。
            </p>

            <div className="mt-6 space-y-3">
              <a
                href="mailto:feedback@chiguachannel.com"
                className="flex items-center gap-3 p-3 rounded-xl bg-background-50 border border-background-200 hover:border-primary-200 transition-colors"
              >
                <span className="w-10 h-10 rounded-lg bg-primary-100 text-primary-600 flex items-center justify-center shrink-0">
                  <i className="ri-mail-line text-lg"></i>
                </span>
                <span className="text-foreground-700 text-sm">feedback@chiguachannel.com</span>
              </a>
              <div className="flex items-center gap-3 p-3 rounded-xl bg-background-50 border border-background-200">
                <span className="w-10 h-10 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center shrink-0">
                  <i className="ri-wechat-line text-lg"></i>
                </span>
                <span className="text-foreground-700 text-sm">吃瓜频道官方瓜友群（投稿优先通道）</span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={onSubmit}
              data-readdy-form="吃瓜频道互动投稿表单"
              className="rounded-2xl bg-background-50 border border-background-200 p-6 md:p-8"
            >
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground-800 mb-1.5">你的称呼</label>
                  <input
                    type="text"
                    name="name"
                    required
                    maxLength={50}
                    placeholder="怎么称呼你"
                    className="w-full h-11 rounded-lg bg-background-100 border border-background-200 px-3.5 text-sm outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 placeholder:text-foreground-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground-800 mb-1.5">联系方式（邮箱）</label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="用于接收编辑部回复"
                    className="w-full h-11 rounded-lg bg-background-100 border border-background-200 px-3.5 text-sm outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 placeholder:text-foreground-400"
                  />
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-foreground-800 mb-1.5">互动类型</label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {["提供猛料", "投稿选题", "内容更正", "随便聊聊"].map((opt) => (
                    <label
                      key={opt}
                      className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-700 cursor-pointer hover:border-primary-300"
                    >
                      <input type="checkbox" name="topic" value={opt} className="accent-[oklch(var(--primary-500))] w-4 h-4" />
                      {opt}
                    </label>
                  ))}
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-foreground-800 mb-1.5">
                  内容描述 <span className="text-foreground-400 font-normal">（500 字以内）</span>
                </label>
                <textarea
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  placeholder="请尽量描述清楚时间、人物与线索来源，方便编辑部核实与跟进……"
                  className="w-full rounded-lg bg-background-100 border border-background-200 px-3.5 py-3 text-sm outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 placeholder:text-foreground-400 resize-none"
                ></textarea>
              </div>

              <div className="mt-2" aria-hidden="true" style={{ display: "none" }}></div>
              {/* honeypot */}
              <input
                type="text"
                name="company_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              {status === "success" && (
                <p className="mt-4 flex items-center gap-2 text-sm text-primary-700 bg-primary-50 border border-primary-200 rounded-lg px-4 py-3">
                  <i className="ri-checkbox-circle-fill"></i> 收到啦！感谢你的分享，编辑部会尽快阅读并联系你。
                </p>
              )}
              {status === "error" && (
                <p className="mt-4 flex items-center gap-2 text-sm text-primary-700 bg-primary-50 border border-primary-200 rounded-lg px-4 py-3">
                  <i className="ri-error-warning-fill"></i> {formError || "提交未成功，请稍后再试。"}
                </p>
              )}

              <div className="mt-5 flex flex-col sm:flex-row items-center gap-3 justify-between">
                <p className="text-xs text-foreground-400 leading-relaxed">
                  你的信息仅用于频道与编辑部沟通，不会被公开或用于营销。
                </p>
                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="w-full sm:w-auto px-6 h-11 rounded-lg bg-primary-500 text-background-50 font-semibold text-[15px] whitespace-nowrap hover:bg-primary-600 disabled:opacity-60 transition-colors"
                >
                  {status === "loading" ? "正在提交…" : "提交互动 / 投稿"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}